import { PayloadAction } from '@reduxjs/toolkit';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import EdsDebugService from '@/features/eds/services/EdsDebugService/EdsDebugService';
import { SortDirection } from '@/enums/SortDirection';
import HttpService from '@/services/HttpService/HttpService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import {
  selectRecentActivityListPagination,
  selectRecentActivitySavedFiltersForSending,
} from '../store/recentActivitySelectors';
import {
  getRecentActivityList,
  getRecentActivityListSuccess,
  incrementRecentActivityListPage,
} from '../store/recentActivitySlice';

function* getRecentActivityListSaga({ type, payload }: PayloadAction<{ id: string }>) {
  try {
    const pagination = yield select(selectRecentActivityListPagination);
    const savedFilters = yield select(selectRecentActivitySavedFiltersForSending);

    const hasEntityTypeFilter =
      Array.isArray(savedFilters?.filters) &&
      savedFilters.filters.some((filter) => filter.propertyName === 'EntityType');

    const requestUrl = hasEntityTypeFilter
      ? `/filter/${EdsEntityCodes.EntityRecentActivity}?${QueryParamsService.constructSearchParamsFromRecord({
          page: pagination.page,
          perPage: pagination.perPage,
          sortBy: 'CreatedAt',
          sortDirection: SortDirection.DESC,
        })}&includes=RecentActivity,FieldChanges,Metadata`
      : `/examiner/recent-activity/all/${payload.id}/ClaimSet?${QueryParamsService.constructSearchParamsFromRecord({
          page: pagination.page,
          perPage: pagination.perPage,
          sortBy: 'CreatedAt',
          sortDirection: SortDirection.DESC,
        })}&includes=RecentActivity,FieldChanges,Metadata`;

    const requestMethod = hasEntityTypeFilter ? HttpService.post : HttpService.get;
    const requestBody = hasEntityTypeFilter ? savedFilters.filters : [];
    const requestConfig = hasEntityTypeFilter ? { baseURL: import.meta.env.VITE_API_URL_EDS } : {};

    const { data } = yield call(requestMethod, requestUrl, requestBody, requestConfig);

    if (data) {
      const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings);

      const transformedResponse = normalizedResponse.map((item) => ({ ...item, ...item.entities }));

      EdsDebugService.logResponse(EdsEntityCodes.EntityRecentActivity, {
        rawResponse: data,
        normalizedResponse,
      });

      yield put(
        getRecentActivityListSuccess({
          shouldRewrite: type !== incrementRecentActivityListPage.type,
          data: {
            listings: transformedResponse,
            pagination: {
              ...data.pagination,
              isLastPage: pagination.page * pagination.perPage >= pagination.numberOfItems,
            },
          },
        }),
      );
    }
  } catch (error) {
    console.error(error);
  }
}

export function* recentActivityListWatcher() {
  yield takeLatest([getRecentActivityList.type, incrementRecentActivityListPage.type], getRecentActivityListSaga);
}
